import { BillingStatus } from '../components/admin/billing/billingConstants';
import { ArchiveProjectStatus } from '../definitions/enums/archiveEnums';
import { ExternalSupervisorInvitationStatus } from '../definitions/enums/externalSupervisorInvitation';
import { AdminPermission } from '../definitions/enums/permissions';
import {
    SubmissionErrorType,
    SubmissionStatus,
} from '../definitions/enums/plagiarismCheckerEnums';
import { ResearchAreaFormat } from '../definitions/enums/researchArea';
import {
    RubricScoringType,
    RubricType,
} from '../definitions/enums/rubricEnums';
import { IProjectFile } from '../definitions/interfaces/projectFileTypes';
import {
    InvitationStatus,
    ProjectEditableDatesEnum,
    ProjectSecondSupervisorStatus,
    ProjectStatus,
    ProjectUserChangesStatusEnum,
} from '../utils/utils';
import {
    AdminApprovalOutcomeEnum,
    ChangeProjectVersion,
    NewValueChangeType,
} from './projectVersionHistoryChangeType';

import {
    TopicParticipant,
    UserRole,
    GradingSupervisor,
    IProjectTimeline,
    ITopicMessage,
} from 'susy-common/models';
import { ITopicDisplayParticipant } from '../components/projectTopic/interfaces';

import { ProjectTimelineType } from 'susy-common/enums';

export interface User {
    _id: string;
    email: string;
    firstName: string;
    lastName: string;
    intakes?: Intake[];
    clmId?: string;
    userRole: UserRole;
    creationDate: Date;
    profilePictureId: string;
    programs?: Program[];
    faculties?: Faculty[];
    departments?: Department[];
    supervisorGroups?: SupervisorGroup[];
    temporaryPasswordCode?: string;
    waitingDelete: boolean;
    deleteDateTime?: Date;
    lastLoginDateTime?: Date;
    firstTimeLogin?: boolean;
    benchmark?: number;
    externalSupervisorInvitation?: ExternalSupervisorInvitation;
    isExternalSupervisor?: boolean;
    projectsSupervisionCapacity?: number;
    profile?: UserProfile;
    permissions: Array<AdminPermission>;
    lastSeenVersion: string;
    topicsWithUnreadMessages?: Array<string>;
    numberOfAssignedActiveProjects?: number;
}

export interface UserWithProfilePicture {
    _id: string;
    profilePictureId: string;
}

export enum AcademicDegree {
    Bachelor = 'Bachelor',
    Master = 'Master',
    PhD = 'PhD',
}

export interface IExternalSupervisorDeclinedInvitationData {
    firstName: string;
    lastName: string;
    creationDate: Date;
    date: Date;
    reason: string;
    declinedBy: User;
}

export interface ExternalSupervisorInvitation {
    _id: string;
    invitationId: string;
    invitedBy: User;
    project: ProjectModel;
    status: ExternalSupervisorInvitationStatus;
    declined?: IExternalSupervisorDeclinedInvitationData;
}

export interface UserProfile {
    phone?: string;
    currentOccupation?: string;
    employer?: string;
    academicDegree?: string;
    academicDegreeProof?: AcademicDegreeProofDocument;
}

export interface AcademicDegreeProofDocument {
    _id: string | null;
    filename: string;
    size: number;
    originalname: string;
    destination: string;
}

export interface Publication {
    userId: string | null;
    title: string;
    type: string;
    publicationDate: Date;
    description: string;
    link: string;
    publicationFile: File | null;
}

export interface ProfileType {
    profilePictureId: string;
    publications: Array<Publication>;
}

export interface PostDispatch {
    type: string;
    payload: object;
}

export interface SupervisorProposal {
    _id: string | null;
    supervisorUser: User;
    status: string;
    changesReviewed?: boolean;
    declineDescription?: string;
    usersSeenDeclineMessage?: Array<string>;
}

export interface PlagiarismCheck {
    submission_id: string;
    overall_match_percentage: number;
    internet_match_percentage: number;
    publication_match_percentage: number;
    submitted_works_match_percentage: number;
    status: SubmissionStatus;
    time_requested: Date;
    time_generated: Date; //integration returns string
    top_source_largest_matched_word_count: number;
    top_matches: [
        {
            name: string;
            percentage: number;
        }
    ];
    metadata: {
        custom: Record<string, unknown>;
    };
    errorMessage?: string;
    errorType?: SubmissionErrorType;
    retryTimes: number;
}

export interface ArchivedProjectUser {
    _id?: string;
    userId?: string;
    firstName: string;
    lastName: string;
}

export interface ArchivedProjectTeamMember {
    member: ArchivedProjectUser;
    teamMemberTasks: string;
    invitationStatus: InvitationStatus;
}

export interface ArchivedProject {
    _id: string | null;
    isVisibleToEveryone: boolean;
    archiveProjectType: ArchiveProjectStatus;
    title: string;
    overview: string;
    literature: Array<string>;
    supervisor: ArchivedProjectUser;
    secondSupervisors?: Array<ArchiveSecondSupervisor>;
    taskForOwnerInGroup: string;
    teamList: Array<ArchivedProjectTeamMember>;
    dates: ProjectDates;
    proposedBy: ArchivedProjectUser;
    researchArea: ResearchArea;
    isDisclosureAgreementAttached: boolean;
    files: Array<IProjectFile>;
    individualGrading?: boolean;
    additionalSupervisor?: ArchivedProjectUser;
    secondAttempt: {
        secondAttemptStudentIds: Array<string>;
    };
    projectTypeSettings: IArchivedProjectTypeSettings;
    intake: IArchivedProjectIntake;
    grading: IArchiveProjectGrading[];
    projectTimeline: IArchivedProjectTimeline;
    plagiarismCheck: IArchivedPlagiarismCheck;
}

export interface IArchivedPlagiarismCheck {
    _id: string;
    status: SubmissionStatus;
    errorType: SubmissionErrorType;
    retryTimes: number;
    errorMessage: string;
    overall_match_percentage: number;
    internet_match_percentage: number;
    publication_match_percentage: number;
    submitted_works_match_percentage: number;
}

export interface IArchivedProjectTimeline {
    _id: string;
    name: string;
    type: ProjectTimelineType;
    registrationKickoffDate: Date;
    registrationDeadlineDate: Date;
    definedKickoffDate: Date;
    definedDeadlineDate: Date;
    dynamicWorkingTimeUseDefaultSetting: boolean;
    dynamicKickoffAmountOfDays: number;
    evaluationUseDefaultSetting: boolean;
    evaluationAmountOfDays: number;
}

interface IArchivedProjectScoringGrading {
    supervisor: IArchivedProjectUser;
    rubric: EvaluationRubric;
    submittedGrade: boolean;
    criterias: Array<{
        criteriaId: string;
        points: number;
        comment: string;
    }>;
    remark: string;
    files: Array<IProjectFile>;
    directlyEnteredTotalPoints: string;
    areCustomMaxPointsEnteredForCriteria: boolean;
}

interface IArchivedProjectNonScoringGrading {
    supervisor: IArchivedProjectUser;
    rubric: EvaluationRubric;
    submittedGrade: boolean;
    criterias: Array<{
        criteriaId: string;
        criteriaLevelId: string;
        comment: string;
    }>;
    remark: string;
}

interface IArchiveProjectGrading {
    supervisorsAcceptedGrade: IArchivedProjectUser[];
    supervisorsNotAgreeingOnGrade: IArchivedProjectUser[];
    scoring: IArchivedProjectScoringGrading[];
    nonScoring: IArchivedProjectNonScoringGrading[];
    finalGrade: string;
    gradePoints: string;
    studentId?: string;
}

export interface IArchivedProjectIntake {
    _id: string;
    clmId: string;
    name: string;
    program: IArchivedProjectProgram;
    courses: IArchivedProjectIntakeCourse[];
}

interface IArchivedProjectIntakeCourse {
    clmId: string;
    name: string;
}

interface IArchivedProjectProgram {
    _id: string;
    name: string;
    faculty: IArchivedProjectFaculty;
}

interface IArchivedProjectFaculty {
    _id: string;
    name: string;
}

export interface IArchivedProjectTypeSettings {
    _id?: string;
    projectTypeName: string;
    userRequirements: string;
    expectedTime: string;
    expectedWork: string;
    proposalSettings: IProposalSettings;
    groupProjectsSettings: IGroupSettings;
    supervisorSettings: ISupervisorSettings;
    userSettings: IUserSettings;
    projectSettings: IProjectSettings;
    gradingSettings: IGradingSettings;
    rubrics: IProjectSettingsRubrics;
    intakes: IArchivedProjectIntake[];
    intakeCourse?: IArchivedProjectIntakeCourse | null;
}

export type ArchivedProjectBrowserModel = {
    _id: string | null;
    isVisibleToEveryone: boolean;
    archiveProjectType: ArchiveProjectStatus;
    title: string;
    supervisor: ArchivedProjectUser;
    secondSupervisors?: Array<ArchiveSecondSupervisor>;
    teamList: Array<ArchivedProjectTeamMember>;
    dates: ProjectDates;
    proposedBy: ArchivedProjectUser;
    researchArea: ResearchArea;
    isDisclosureAgreementAttached: boolean;
};

export interface ArchiveSecondSupervisor {
    firstName: string;
    lastName: string;
    userId: string;
    status?: ProjectSecondSupervisorStatus;
}

export interface IProjectRegistrationModel {
    isAdminAppovalRequired: boolean;
    requestStatus?: AdminApprovalOutcomeEnum;
    declineDescription?: string;
    usersSeenDeclineMessage?: string[];
    admin?: User;
}

export type ProjectBrowserModel = {
    _id: string | null;
    title: string;
    supervisor: User;
    secondSupervisorProposals: SupervisorProposal[];
    additionalSupervisor?: User;
    teamList: Array<TeamMember>;
    currentStatus: ProjectStatus;
    dates: ProjectDates;
    proposedBy: User;
    isDisclosureAgreementAttached: boolean;
    intake: Intake;
    projectTimeline?: ProjectTimeline;
    extensionRequests?: Array<ExtensionRequest>;
    plagiarismCheck?: PlagiarismCheck;
    grading: IGrade[];
    isAdminApproveRequired?: boolean;
    hasAdminDeclined?: boolean;
    projectRegistration?: IProjectRegistrationModel;
    secondAttempt?: SecondAttempt;
    requiresAdditionalSupervisor?: boolean;
};

export interface ProjectModel {
    _id: string | null;
    projectTypeSettings: IProjectTypeSettings;
    title: string;
    literature: Array<string>;
    supervisor: User;
    secondSupervisorProposals: SupervisorProposal[];
    taskForOwnerInGroup: string;
    teamList: Array<TeamMember>;
    currentStatus: ProjectStatus;
    dates: ProjectDates;
    proposedBy: User;
    researchArea: ResearchArea;
    groupProject: boolean;
    isDisclosureAgreementAttached: boolean;
    files: Array<IProjectFile>;
    changesReviewed: boolean;
    declineDescription: string;
    intake: Intake;
    projectTimeline?: ProjectTimeline;
    extensionRequests?: Array<ExtensionRequest>;
    plagiarismCheck?: PlagiarismCheck;
    grading: IGrade[];
    isAdminApproveRequired?: boolean;
    hasAdminDeclined?: boolean;
    projectRegistration?: IProjectRegistrationModel;
    userChanges?: IProjectUserChanges;
    disableGradingReport?: boolean;
    secondAttempt: SecondAttempt;
    projectFailed: ProjectFailed;
    additionalSupervisor?: User;
    individualGrading?: boolean;
    requiresAdditionalSupervisor?: boolean;
}

type SecondAttempt = {
    adminApprovalRequired: boolean;
    secondAttemptStudents: Array<string>;
};

export interface ProjectFailed {
    reason: string;
    isFailedByAdmin: boolean;
    lastStateBeforeFailed: {
        lastStatus: ProjectStatus;
        gradePoints: Array<string>;
    };
}

export interface TeamMember {
    member: User;
    teamMemberTasks: string;
    invitationStatus: InvitationStatus;
}

export interface ProjectDates {
    deadlineDates?: DeadlineDates | null;
    proposed: Date | null;
    approved: Date | null;
    declined: Date | null;
    registered: Date | null;
    handedIn: Date | null;
    saved: Date | null;
    withdrawn: Date | null;
    evaluated: Date | null;
    failed: Date | null;
}

export interface IDocument {
    _id: string | null;
    filename: string;
    size: number;
    originalname: string;
    destination: string;
}

export interface DeadlineDates {
    submission: Date | null;
    evaluation: Date | null;
}

export interface IAdminChangeProjectDatesPayload {
    newProjectDateValue?: Date;

    projectEditedDate: ProjectEditableDatesEnum;
    comment: string;
}

export interface ProjectTimeline extends IProjectTimeline {
    intakes: Array<Intake>;
}

export interface Organization {
    _id: string | null;
    name: string;
    admins?: Array<User>;
    default?: boolean;
    clmId?: string;
}

export type University = Organization;

export interface Faculty extends Organization {
    projectTimeLine?: Array<ProjectTimeline>;
    users?: Array<User>;
    default?: boolean;
    applyTimelinesForChildren?: boolean;
    applyAdminsForChildren?: boolean;
}

export interface Program extends Organization {
    faculty?: Faculty;
    projectTimeLine?: Array<ProjectTimeline>;
    users?: Array<User>;
    default?: boolean;
    applyTimelinesForChildren?: boolean;
    applyAdminsForChildren?: boolean;
}

export interface IntakeCourse {
    clmId: string;
    name: string;
}

export interface Intake extends Organization {
    program?: Program;
    projectTimeLine?: Array<ProjectTimeline>;
    users?: Array<User>;
    default?: boolean;
    courses?: Array<IntakeCourse>;
}

export interface Department extends Organization {
    supervisors?: Array<User>;
    applyAdminsForChildren?: boolean;
    applySupervisorsForChildren?: boolean;
    default?: boolean;
}

export interface SupervisorGroup extends Organization {
    parent?: Department;
    supervisors?: Array<User>;
    default?: boolean;
}

export interface ResearchArea {
    _id: string | null;
    title: string;
    keywords: Array<string>;
    supervisor: User;
    format: ResearchAreaFormat;
    intakes: Array<Intake>;
    overview: string;
    dateCreated: Date;
    visible?: boolean;
    inUse?: boolean;
}

export interface Notifications {
    _id: string | null;
    title: string;
    time: string;
}

export interface ProjectOverview {
    projects: Array<ProjectModel>;
    invites: Array<ProjectModel>;
}

export interface ExtensionRequest {
    _id: string;
    state: string | null;
    sendDate: Date;
    receiveDate: Date | null;
    answerDate: Date | null;
    project: ProjectModel;
    user: User;
    fileOfProofType: string | null;
    fileOfProof: Array<IDocument>;
    numberOfDaysForExtension: number | null;
    rejectionExplanation: string | null;
    answerConfirmedByUsers?: Array<User>;
}

export interface IAdminDashboardData {
    projects: Array<ProjectBrowserModel>;
    researchAreas: Array<ResearchArea>;
    pendingExternalSupervisorInvitations: Array<ExternalSupervisorInvitation>;
    failedPlagiarismChecks: Array<PlagiarismCheck>;
    intakes: Array<Intake>;
}

export interface SystemMessage {
    title: string;
    message: string;
}

export interface EvaluationCriteriaLevel {
    _id?: string;
    title: string;
    description: string;
}

export interface EvaluationCriteria {
    _id?: string;
    title: string;
    description: string;
    criteriaLevels: Array<EvaluationCriteriaLevel>;
    maxPoints?: string;
}

export interface EvaluationRubricBase {
    _id?: string;
    title: string;
    type: RubricType;
    scoringType: RubricScoringType;
    maxPoints: string;
    criterias: Array<EvaluationCriteria>;
    comment?: string;
}

export interface EvaluationRubric extends EvaluationRubricBase {
    projectTypeNames: Array<string>;
    published: boolean;
    addPointsManually: boolean;
    inUse?: boolean;
}

export interface GradingSchemaBase {
    _id?: string;
    name: string;
    grades: Array<Grade>;
}

export interface GradingSchema extends GradingSchemaBase {
    inUse: boolean;
    projectTypeNames: Array<string>;
}

export interface Grade {
    name: string;
    percentages: { start: number; end: number };
}

export interface Error {
    componentName?: string;
    message?: string;
    type?: string;
}

export interface ProjectVersionsHistory {
    projectId: string;
    changes: Array<ChangeProjectVersion<NewValueChangeType>>;
}

export interface ConfigurationType {
    featureSwitch: FeatureSwitchType;
    turnItInPluginMaxRetries: number;
}

export interface FeatureSwitchType {
    turnItInPlugin: boolean;
    adminUserDelete: boolean;
    addClmUsersManually: boolean;
    billing: boolean;
    languageSwitcher: boolean;
    addIntakesManually: boolean;
    adminCreateUserRoleAccount: boolean;
    gradingOptionalAssessment: boolean;
    gradingOptionalAgreeDisagree: boolean;
}

export interface ProjectIndexMap {
    project: ProjectModel;
    index: number;
}

export interface IScoring {
    rubric: EvaluationRubric;
    supervisor: GradingSupervisor;
    submittedGrade?: boolean;
    criterias: Array<{
        criteriaId: string;
        points: number;
        comment: string;
        maxPointsBySupervisor?: string;
    }>;
    sharedAssessment: {
        requestedSharedAssessment: boolean;
        supervisorsAccepted: GradingSupervisor[];
        supervisorsRejected: GradingSupervisor[];
    };
    directlyEnteredTotalPoints?: string;
    areCustomMaxPointsEnteredForCriteria?: boolean;
    remark: string;
    files?: IDocument[];
}

export interface INonScoring {
    rubric: EvaluationRubric;
    supervisor: GradingSupervisor;
    submittedGrade?: boolean;
    criterias: Array<{
        criteriaId: string;
        criteriaLevelId: string;
        comment: string;
    }>;
    remark: string;
}

export interface IGrade {
    studentId?: string;
    scoring: IScoring[];
    nonScoring: INonScoring[];
    supervisorsAcceptedGrade: GradingSupervisor[];
    supervisorsNotAgreeingOnGrade: GradingSupervisor[];
    finalGrade?: string;
    gradePoints?: string;
}

export interface IGrading {
    rubrics: {
        scoring: EvaluationRubric;
        nonScoring: EvaluationRubric;
        presentationRubric?: EvaluationRubric;
    };
    grading: Array<IGrade>;
}

export interface CriteriaNonScoringCards {
    [fieldName: string]: CriteriaNonScoringCardsFields;
}

export interface CriteriaNonScoringCardsFields {
    _id?: string;
    selectedCriteriaLevelIndex: number | null;
    selectedCriteriaLevelId: string | undefined;
    comment: string;
}

export interface CriteriaScoringCards {
    [fieldName: string]: CriteriaScoringCardsFields;
}

export interface CriteriaScoringCardsFields {
    _id?: string;
    points: string;
    comment: string;
    maxPointsBySupervisor?: string;
}

export interface IBillingData {
    name: string;
    email: string;
    address: string;
    city: string;
    state: string;
    postalCode: string;
    country: { value: string; label: string } | null;
    registryNumber?: string;
    taxNumber?: string;
    department?: string;
    isCompany: boolean;
}

export interface ICardData {
    cardType: string;
    last4: string;
    expMonth: number;
    expYear: number;
}

export interface IMonthlyCosts {
    admins: {
        pricePerUnit: number;
        numberOfAdmins: number;
    };
    students: {
        pricePerUnit: number;
        numberOfStudents: number;
    };
    supervisors: {
        pricePerUnit: number;
        numberOfInternalSupervisors: number;
        numberOfExternalSupervisors: number;
    };
}

export interface IInvoiceListItem {
    id: string;
    amountDue: number;
    status: BillingStatus;
    invoiceDueMonth: Date;
    pdfLink: string;
    totalNumOfAccounts: number;
}

export interface IBillingDashboardData {
    currentMonthCosts: IMonthlyCosts;
    nextMonthCostsPrediction: IMonthlyCosts;
    billingDetails: IBillingData | null;
    cardDetails: ICardData | null;
    invoices: Array<IInvoiceListItem>;
}

export interface IBillingDashboardActionPayload {
    currentMonthCosts?: IMonthlyCosts;
    nextMonthCostsPrediction?: IMonthlyCosts;
    billingDetails?: {
        name: string;
        email: string;
        address: string;
        city: string;
        state: string;
        postalCode: string;
        country: string;
        registryNumber?: string;
        taxNumber?: string;
        department?: string;
        isCompany: boolean;
    };
    cardDetails?: ICardData;
    invoices?: Array<IInvoiceListItem>;
}

export type ProjectSettingsWithRestrictions = {
    projectSettings: IProjectTypeSettings[];
    restrictedIntakeIds: string[];
};
export type CurrentProjectSettingsReducer = {
    projectSettings: IProjectTypeSettings;
    restrictedIntakeIds: Array<string>;
};

export interface GeneralAppSettings {
    logoFilename: string;
    logoLink: string;
    organizationName: string;
    generalTimelineSettings: IGeneralTimelineSettings;
    isPublishingProjectsAllowed: boolean;
}

export interface IGeneralTimelineSettings {
    defaultNumberOfEvaluationDays: number;
    defaultNumberOfSubmissionTimeDays: number;
}

export interface GeneralAppSettingsRequest {
    logoFilename: string | undefined;
    organizationName: string | undefined;
    generalTimelineSettings: IGeneralTimelineSettings;
}

export interface IProjectTypeSettings {
    _id: string | undefined;
    visible: boolean;
    projectTypeName: string;
    userRequirements: string;
    expectedTime: string;
    expectedWork: string;
    rubrics: IProjectSettingsRubrics;
    intakes: Array<Intake>;
    intakeCourse?: IntakeCourse | null;
    proposalSettings: IProposalSettings;
    projectSettings: IProjectSettings;
    groupProjectsSettings: IGroupSettings;
    supervisorSettings: ISupervisorSettings;
    userSettings: IUserSettings;
    gradingSettings: IGradingSettings;
    isLegacyProjectType?: boolean;
    isHiddenFromAll?: boolean;
}

export interface IProjectSettingsRubrics {
    gradingSchema: GradingSchema | null;
    scoringRubric: EvaluationRubric | null;
    nonScoringRubric: EvaluationRubric | null;
    presentationRubric: EvaluationRubric | null;
}

export interface IProjectSettings {
    allowProjectSubmissionAfterDeadline: boolean;
    finalGradePointsDeduction: number;
    highestPossibleGradeDeduction: number;
    projectRegistrationRequiresApproval: boolean;
    projectRequiresPresentation: boolean;
    thresholdPercentageForFailedProject: number;
}

export interface ISupervisorSettings {
    allowExternalSupervisors: boolean;
    requireAtLeastOneInternalSupervisor: boolean;
    numberOfRequiredSupervisors: number;
}

export interface IProposalSettings {
    requireResearchArea: boolean;
    requireCitation: boolean;
    requireAdditionalDocuments: boolean;
}

export interface IGroupSettings {
    allowGroupProjects: boolean;
    maxNumberOfUsersForGroupProjects: number;
    allowGroupProjectsWithUsersFromDifferentIntakes: boolean;
}

export interface IUserSettings {
    maxNumberOfProjects: number;
    maxNumberOfProposals: number;
}

export interface IGradingSettings {
    firstSupervisorGradesScoringRubric: boolean;
    firstSupervisorGradesNonScoringRubric: boolean;
    secondSupervisorGradesScoringRubric: boolean;
    secondSupervisorGradesNonScoringRubric: boolean;
    secondSupervisorHasToAgreeDisagreeWithFirstSupervisor: boolean;
}

export const getInitialProjectTypeSettingsState = (): IProjectTypeSettings => {
    return {
        _id: undefined,
        projectTypeName: '',
        userRequirements: '',
        expectedTime: '',
        expectedWork: '',
        intakes: [],
        rubrics: getInitialProjectSettingsRubrics(),
        proposalSettings: getInitialProposalSettingsState(),
        projectSettings: getInitialProjectSettingsState(),
        groupProjectsSettings: getInitialGroupSettingsState(),
        supervisorSettings: getInitialSupervisorSettings(),
        userSettings: getInitialUserSettings(),
        gradingSettings: getInitialGradingSettings(),
        visible: true,
    };
};

export const getInitialGradingSchema = (): GradingSchema => {
    return {
        name: '',
        grades: [],
        inUse: false,
        projectTypeNames: [],
    };
};

export const getInitialUserSettings = (): IUserSettings => {
    return {
        maxNumberOfProjects: 1,
        maxNumberOfProposals: 1,
    };
};

export const getInitialProjectSettingsState = (): IProjectSettings => {
    return {
        allowProjectSubmissionAfterDeadline: false,
        finalGradePointsDeduction: 1,
        highestPossibleGradeDeduction: 100,
        projectRegistrationRequiresApproval: false,
        projectRequiresPresentation: false,
        thresholdPercentageForFailedProject: 50,
    };
};

export const getInitialGroupSettingsState = (): IGroupSettings => {
    return {
        allowGroupProjects: false,
        maxNumberOfUsersForGroupProjects: 1,
        allowGroupProjectsWithUsersFromDifferentIntakes: false,
    };
};

export const getInitialProjectSettingsRubrics = (): IProjectSettingsRubrics => {
    return {
        gradingSchema: null,
        nonScoringRubric: null,
        scoringRubric: null,
        presentationRubric: null,
    };
};

export const getInitialProposalSettingsState = (): IProposalSettings => {
    return {
        requireResearchArea: false,
        requireCitation: false,
        requireAdditionalDocuments: false,
    };
};

export const getInitialSupervisorSettings = (): ISupervisorSettings => {
    return {
        allowExternalSupervisors: false,
        requireAtLeastOneInternalSupervisor: false,
        numberOfRequiredSupervisors: 2,
    };
};

export const getInitialRubric = (): EvaluationRubric => {
    return {
        criterias: [],
        title: '',
        maxPoints: '',
        addPointsManually: false,
        type: RubricType.NotDefinedPoints,
        scoringType: RubricScoringType.Scoring,
        projectTypeNames: [],
        published: false,
    };
};

export const getInitialEvaluationRubric = (): EvaluationRubric => {
    return {
        projectTypeNames: [],
        published: true,
        addPointsManually: false,
        title: 'evaluation-rubric',
        type: RubricType.DefinedPoints,
        scoringType: RubricScoringType.Scoring,
        maxPoints: '100',
        criterias: [],
    };
};

export const getInitialGradingSettings = (): IGradingSettings => {
    return {
        firstSupervisorGradesScoringRubric: true,
        firstSupervisorGradesNonScoringRubric: false,
        secondSupervisorGradesScoringRubric: true,
        secondSupervisorGradesNonScoringRubric: true,
        secondSupervisorHasToAgreeDisagreeWithFirstSupervisor: true,
    };
};

export interface VersionType {
    version: string;
}

export interface IProjectUserChanges {
    title?: string;
    status: ProjectUserChangesStatusEnum;
    researchAreaTitle?: string;
    researchAreaOverview?: string;
    literature?: string[];
    declineDescription?: string;
}

export interface ITopic {
    _id?: string;
    project: ProjectModel | string;
    title: string;
    createdBy?: User;
    participants: TopicParticipant[];
    creationDate: Date;
    messageFeed?: string;
    latestMessage?: { text: string; time?: Date };
}

export interface IArchivedProject {
    _id?: string;
    title: string;
}

export interface IArchivedTopic {
    _id?: string;
    project: IArchivedProject;
    title: string;
    participants: IArchivedProjectUser[];
    creationDate: Date;
    messageFeed?: string;
    latestMessage?: { text: string; time?: Date };
}

export interface IArchivedProjectUser {
    firstName: string;
    lastName: string;
    userId: string;
}

export interface IArchivedTopicMessageFeed {
    _id?: string;
    projectTopic: string;
    messages: IArchivedTopicMessage[];
}

export interface IArchivedTopicMessage {
    text: string;
    sentBy: IArchivedProjectUser;
    sendTime: Date;
}

export interface ITopicMessageFeed {
    _id?: string;
    projectTopic: string;
    messages: ITopicMessage[];
}

export interface ISearchResultTopic {
    _id?: string;
    project: ProjectModel;
    title: string;
    createdBy?: User;
    participants: ITopicDisplayParticipant[];
    creationDate: Date;
    messageFeed?: string;
    relevantMessage?: { text: string; time: Date };
}

export interface ISearchResultArchivedTopic {
    _id?: string;
    project: IArchivedProject;
    title: string;
    createdBy?: ITopicDisplayParticipant;
    participants: ITopicDisplayParticipant[];
    creationDate: Date;
    messageFeed?: string;
    relevantMessage?: { text: string; time: Date };
    isArchived?: boolean;
}
